import * as React from "react";
import { PanelLeft } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Button, type ButtonProps } from "@/shared/ui/kit/button";
import { Sheet, SheetContent, SheetTitle } from "@/shared/ui/kit/sheet";
import { SIDEBAR_WIDTH_MOBILE, useSidebar, type SidebarCssVars } from "@/shared/ui/kit/sidebar-context";

// Port do shadcn para Tailwind 3: sem cookie (estado vem de fora) e sem Ctrl/Cmd+B, que e negrito no editor.

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    mobileTitle: string;
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(({ mobileTitle, className, children, ...props }, ref) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
    const returnFocusRef = React.useRef<HTMLElement | null>(null);

    if (isMobile) {
        const mobileVars: SidebarCssVars = { "--sidebar-width": SIDEBAR_WIDTH_MOBILE };
        return (
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
                <SheetContent
                    side="left"
                    hideClose
                    aria-describedby={undefined}
                    data-mobile="true"
                    onOpenAutoFocus={() => {
                        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                    }}
                    onCloseAutoFocus={(event) => {
                        // Sem Dialog.Trigger o Radix nao sabe quem abriu a gaveta.
                        event.preventDefault();
                        returnFocusRef.current?.focus();
                    }}
                    style={mobileVars}
                    className="w-[--sidebar-width] max-w-[85vw] gap-0 border-white/10 bg-night_input p-0 text-white/70 dark:bg-night"
                >
                    <SheetTitle className="sr-only">{mobileTitle}</SheetTitle>
                    <div className="flex h-full w-full flex-col">{children}</div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <div
            data-slot="sidebar"
            data-state={state}
            data-collapsible={state === "collapsed" ? "icon" : ""}
            className="group peer hidden md:block"
        >
            {/* Ocupa no fluxo a largura que o container fixo cobre. */}
            <div className="relative w-[--sidebar-width] bg-transparent transition-[width] duration-slow ease-in-out group-data-[collapsible=icon]:w-[--sidebar-width-icon]" />
            <div
                ref={ref}
                className={cn(
                    "fixed inset-y-0 left-0 z-sidebar hidden h-dvh w-[--sidebar-width] border-r border-white/10 transition-[width] duration-slow ease-in-out md:flex group-data-[collapsible=icon]:w-[--sidebar-width-icon]",
                    className
                )}
                {...props}
            >
                <div data-sidebar="sidebar" className="flex h-full w-full flex-col bg-night_input text-white/70 dark:bg-night">
                    {children}
                </div>
            </div>
        </div>
    );
});
Sidebar.displayName = "Sidebar";

const SidebarTrigger = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, onClick, children, "aria-label": ariaLabel = "Alternar menu", ...props }, ref) => {
        const { toggleSidebar } = useSidebar();

        return (
            <Button
                ref={ref}
                type="button"
                variant="ghost"
                size="icon"
                aria-label={ariaLabel}
                data-sidebar="trigger"
                className={cn("text-current hover:bg-white/10 hover:text-white dark:hover:bg-white/10", className)}
                onClick={(event) => {
                    onClick?.(event);
                    toggleSidebar();
                }}
                {...props}
            >
                {children ?? <PanelLeft size={20} />}
            </Button>
        );
    }
);
SidebarTrigger.displayName = "SidebarTrigger";

export { Sidebar, SidebarTrigger };
export { SidebarProvider, useSidebar } from "@/shared/ui/kit/sidebar-context";
export {
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarSeparator,
} from "@/shared/ui/kit/sidebar-menu";
